import React, { useEffect, useState } from "react";
import type { ContextProps } from '../../../../types';
import { getFieldArrayFromObjectArrayByField, isArrayNullOrEmpty } from "../../../../Utilities/Types/arrayUtilities";
import { braillerQueueApi } from "./braillerQueueApi";
import { orderRequestApi } from "../../../Order/OrderRequest/orderRequestApi";
import { createListFromArrayOfObjects } from "../../../../Utilities/Types/listUtilities";
import type { ListItem } from "../../../../Utilities/Types/listUtilities";
import { PageTitle } from "../../../../Components/Landmarks";
import { LayoutFullBleed } from "../../../../Components/Layout";
import { BraillerQueueForm } from "./BraillerQueueForm";
import { emptyBraillerQueueModel, queueItemModel } from "./braillerQueueFactory";
import { useInputForm } from "../../../../Components/Inputs";
import {
    createConfirmationMessageForDuplicates,
    filterQueueByStatus,
    getAllDuplicateCatalogItemsForStudent,
} from "./braillerQueueUtility";
import { BraillerQueueList } from "./BraillerQueueList";
import { GridCell } from "../../../../Components/Grid";
import { SYSTEM_TYPES } from "../../libraryConstants";
import { getSystemTypeTextByValue } from "../../libraryUtility";
import { STUDENT_APPROVAL_STATUS, STUDENT_STATUS } from "../../../Student/studentConstants";

export const BraillerQueueContainer = ({
    handleApiCall,
    setWarnings,
    userId,
}: ContextProps) => {

    const [braillerQueue, setBraillerQueue] = useState<Record<string, unknown>[]>([]);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));
    const [students, setStudents] = useState<ListItem[]>();
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>();

    const queueForm = useInputForm(emptyBraillerQueueModel);

    const pendingQueue = filterQueueByStatus(braillerQueue, true);
    const isPendingQueueEmpty = isArrayNullOrEmpty(pendingQueue);
    const processingQueue = filterQueueByStatus(braillerQueue, false);

    const handleSelectAll = () => {
        const queueItems = getFieldArrayFromObjectArrayByField(pendingQueue, "braillerQueueId");
        setSelectedItems(new Set(queueItems.map((braillerQueueId: unknown) => String(braillerQueueId))));
    }

    const handleDeselectAll = () => {
        setSelectedItems(new Set([]));
    }

    const handleSelectedItemsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const item = String(event.target.value);
        const updatedSet = new Set(selectedItems || []);

        updatedSet.has(item)
            ? updatedSet.delete(item)
            : updatedSet.add(item);

        setSelectedItems(updatedSet);
    }

    const handleGetQueue = () => {
        handleApiCall(() => braillerQueueApi.getUserQueue(), (results: Record<string, unknown>[]) => {
            const transformedResults = Array.isArray(results)
                ? results.map((item: Record<string, unknown>) => queueItemModel(item))
                : [];
            setBraillerQueue(transformedResults);
        });
    }

    const handleRemoveFromQueue = (braillerQueueItemId: string) => {
        if (confirm(`This will remove PERMANENTLY your request from the Queue. Are you sure you want to continue?`))
            handleApiCall(() => braillerQueueApi.deleteQueueItem(braillerQueueItemId), handleGetQueue);
    }

    const handleAssignQueueItems = async (braillerQueueViewModel: Record<string, unknown>) => {
        const capturedSelectedItems = Array.from(selectedItems);

        braillerQueueViewModel.braillerQueueItemIds = capturedSelectedItems;

        const noItemsSelectedFromPendingQueue = isArrayNullOrEmpty(braillerQueueViewModel.braillerQueueItemIds);
        if (noItemsSelectedFromPendingQueue) {
            setWarnings(["Please select at least one item from the queue to assign."]);
            return;
        }

        const duplicateCatalogItems = getAllDuplicateCatalogItemsForStudent(braillerQueueViewModel, pendingQueue, processingQueue);
        const hasDuplicates = duplicateCatalogItems.length > 0;
        const confirmationMessage = createConfirmationMessageForDuplicates(duplicateCatalogItems, hasDuplicates);

        if (!hasDuplicates || (hasDuplicates && confirm(confirmationMessage))) {
            handleApiCall(() => braillerQueueApi.assignShippingLocation(braillerQueueViewModel), handleGetQueue);
            setSelectedItems(new Set([]));
        }
    }

    useEffect(() => {

        const getStudents = () => {
            handleApiCall(() => orderRequestApi.getStudents({
                userId: userId,
                isActive: true,
                studentApprovalStatus: STUDENT_APPROVAL_STATUS.Approved.id,
                studentStatus: STUDENT_STATUS.Active.id,
                hasSignedParentalConsent: true,
                forDropDown: true,
            }), (results: Record<string, unknown>[]) => {
                setStudents(createListFromArrayOfObjects(results, `studentId`, `fullName`));
            });
        };

        const getShippingLocations = () => {
            handleApiCall(() => orderRequestApi.getShippingLocations(), (results: Record<string, unknown>[]) => {
                setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
            });
        }

        if (!shippingLocations)
            getShippingLocations();
        if (!students)
            getStudents();

        handleGetQueue();
    }, []);

    if (!shippingLocations || !students || !queueForm) return;

    return <>
        <PageTitle h1={`${getSystemTypeTextByValue(SYSTEM_TYPES.Braillers.toString())} Queue`} />

        <GridCell>
            <p>
                If you are submitting a <b>repair request</b>, please send the broken brailler via Free Matter for the
                Blind
                directly to the American Printing House for the Blind, 1839 Frankfort Avenue, Louisville, KY 40206.
                Repaired braillers are returned to FIMC-VI and will NOT be sent to you.
            </p>
            <p>
            Does your broken brailler have a <b>Paige Connect</b> attached? If so, please remove the Paige Connect
            and replace the brailler base prior to sending the brailler to APH for repair. Remember, repaired braillers
            will NOT be sent to you.

            </p>
            <br/>
            <p className="h3 text-center">Please check Brailler Loan History for further instructions.</p>
        </GridCell >

        <LayoutFullBleed className={`catalog`}>
            {
                pendingQueue &&
                <BraillerQueueList
                    queueItems={pendingQueue}
                    handleDeselectAll={handleDeselectAll}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    handleSelectAll={handleSelectAll}
                    handleSelectedItemsChange={handleSelectedItemsChange}
                    selectedItems={selectedItems}
                />
            }
            {
                !isPendingQueueEmpty &&
                <BraillerQueueForm
                    queueForm={queueForm}
                    handleAssignQueueItems={handleAssignQueueItems}
                    setWarnings={setWarnings}
                    shippingLocations={shippingLocations}
                    students={students}
                />
            }
            {
                processingQueue &&
                <BraillerQueueList
                    queueItems={processingQueue}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    isPendingQueue={false}
                />
            }
        </LayoutFullBleed>
    </>;
};
